import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import useUserData from 'hooks/useUserData';
import { pageSelector, setPage } from 'store/reducers/bd-100/bd-100-reducer';

import { Spinner } from '../shared/icons';
import BD100Hero from './BD100Hero';
import BD100Navbar from './BD100Navbar';
import BD100PoliciesModal from './BD100PoliciesModal';
import BD100ResourceLibrary from './BD100ResourceLibrary';
import BD100Teaming from './BD100Teaming';
import useAcceptBd100Terms from './hooks/useAcceptBd100Terms';
import { BD100Container, BD100Wrapper } from './styled';

const BD100SMECouncils = React.lazy(() => import('./BD100SMECouncils'));
const BD100OpportunitiesDashboard = React.lazy<
  React.ComponentType<{ onViewPolicies?: () => void }>
>(() => import('./BD100OpportunitiesDashboard'));

export const BD100_PAGES = {
  OPPORTUNITIES: 'opportunities',
  SME_COUNCILS: 'sme-councils',
  TEAMING: 'teaming',
  RESOURCE_LIBRARY: 'resource-library',
} as const;

export type BD100Pages = (typeof BD100_PAGES)[keyof typeof BD100_PAGES];

export default function BD100() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { push } = useHistory();
  const page = useSelector(pageSelector);
  const userData = useUserData();
  const acceptBd100Terms = useAcceptBd100Terms();

  const isUserDataReady = !!userData?.slug;
  const policiesAccepted = isUserDataReady ? userData.bd100_terms_accepted : null;
  const [showPoliciesModal, setShowPoliciesModal] = useState<boolean>(false);
  const [policiesModalReadOnly, setPoliciesModalReadOnly] = useState(false);

  useEffect(() => {
    if (!isUserDataReady) return;
    if (userData.permissions?.bd100_access !== true) push('/');
  }, [isUserDataReady, userData.permissions?.bd100_access, push]);

  useEffect(() => {
    if (!isUserDataReady) {
      setShowPoliciesModal(false);
      return;
    }

    if (policiesAccepted === false && page === BD100_PAGES.OPPORTUNITIES) {
      setShowPoliciesModal(true);
    } else {
      setShowPoliciesModal(false);
    }
  }, [isUserDataReady, policiesAccepted, page]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const page = query.get('page');

    if (page) {
      dispatch(setPage(page as BD100Pages));
    } else {
      dispatch(setPage(BD100_PAGES.OPPORTUNITIES));
    }
  }, [location]);

  const handleAcceptPolicies = useCallback(() => {
    acceptBd100Terms.mutate(undefined, {
      onSuccess: () => {
        setShowPoliciesModal(false);
      },
    });
  }, [acceptBd100Terms]);

  const handleClosePoliciesModal = useCallback(() => {
    setShowPoliciesModal(false);
    setPoliciesModalReadOnly(false);

    if (policiesAccepted === false && page === BD100_PAGES.OPPORTUNITIES) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', BD100_PAGES.SME_COUNCILS);
      window.history.pushState({}, '', url.toString());
      dispatch(setPage(BD100_PAGES.SME_COUNCILS));
    }
  }, [page, policiesAccepted, dispatch]);

  const handleOpportunityClick = useCallback(() => {
    setPoliciesModalReadOnly(false);
    setShowPoliciesModal(true);
  }, []);

  const handleViewPolicies = useCallback(() => {
    setPoliciesModalReadOnly(true);
    setShowPoliciesModal(true);
  }, []);

  return (
    <BD100Wrapper>
      <BD100Hero />
      <BD100Navbar
        policiesAccepted={policiesAccepted}
        onOpportunityClick={handleOpportunityClick}
      />
      <BD100Container>
        <Suspense fallback={<Spinner />}>
          {page === BD100_PAGES.OPPORTUNITIES && !isUserDataReady ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '240px',
              }}
            >
              <Spinner width={40} height={40} fill="var(--color-primary)" />
            </div>
          ) : null}
          {page === BD100_PAGES.OPPORTUNITIES && policiesAccepted === true ? (
            <BD100OpportunitiesDashboard onViewPolicies={handleViewPolicies} />
          ) : null}
          {page === BD100_PAGES.SME_COUNCILS ? <BD100SMECouncils /> : null}
          {page === BD100_PAGES.TEAMING ? <BD100Teaming /> : null}
          {page === BD100_PAGES.RESOURCE_LIBRARY ? <BD100ResourceLibrary /> : null}
        </Suspense>
      </BD100Container>
      {showPoliciesModal ? (
        <BD100PoliciesModal
          onAccept={handleAcceptPolicies}
          onClose={handleClosePoliciesModal}
          readOnly={policiesModalReadOnly}
        />
      ) : null}
    </BD100Wrapper>
  );
}
